import { lazy, Suspense, useState } from "react";

import { Drawer, DrawerContent, DrawerTopBar } from "@/components/Drawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { TravellerCloseConfirmDrawer } from "@/features/travellers/components/TravellerCloseConfirmDrawer";
import {
  useNewTravellerMutation,
  useUpdateTravellerMutation,
} from "@/features/travellers/hooks/useTravellers";
import type { Traveller } from "@/features/travellers/types/types";

import { useTravellerForm } from "./useTravellerForm";

const TravellerForm = lazy(
  () => import("@/features/travellers/components/TravellerForm"),
);

type TravellerFormDrawerProps = {
  open: boolean;
  onClose: () => void;
  traveller: Traveller | null;
  onSubmitSuccess?: (traveller: Traveller) => void;
};

export function TravellerFormDrawer({
  open,
  onClose,
  traveller,
  onSubmitSuccess,
}: TravellerFormDrawerProps) {
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  const handleMutationSuccess = onSubmitSuccess
    ? (traveller: Traveller) => {
        onSubmitSuccess(traveller);
        onClose();
      }
    : undefined;

  const { mutateAsync: addTraveller, isPending: isAddPending } =
    useNewTravellerMutation({
      onSuccess: handleMutationSuccess,
    });
  const { mutateAsync: editTraveller, isPending: isEditPending } =
    useUpdateTravellerMutation({
      onSuccess: handleMutationSuccess,
    });

  const {
    control,
    isSubmitting,
    title,
    requestClose,
    scanned,
    scanningDrawerOpen,
    openScanningDrawer,
    closeScanningDrawer,
    onScanSuccess,
    onFormSubmit,
  } = useTravellerForm({
    traveller,
    onSubmit: (data) =>
      traveller
        ? editTraveller({ travellerId: traveller.id, data })
        : addTraveller(data),
    onClose: (reason) => {
      if (reason === "cancelWithPendingChanges") {
        setIsCloseConfirmOpen(true);
        return;
      }

      if (reason === "submit" && !!onSubmitSuccess) {
        // if the form was submitted and we have an onSubmitSuccess callback, we want to keep the drawer open until the mutation is successful
        return;
      }

      onClose();
    },
  });

  return (
    <>
      <Drawer
        open={open}
        disableRestoreFocus
        hasFullHeight
        backgroundColor="background"
      >
        <DrawerTopBar onCloseClick={requestClose} title={title} />
        <DrawerContent>
          <Suspense
            fallback={
              <PageLoader>
                <AnimatedLoader icon="users" />
              </PageLoader>
            }
          >
            <TravellerForm
              control={control}
              isSubmitting={isSubmitting || isAddPending || isEditPending}
              scanned={scanned}
              scanningDrawerOpen={scanningDrawerOpen}
              onScanClick={openScanningDrawer}
              onScanningDrawerClose={closeScanningDrawer}
              onScanSuccess={onScanSuccess}
              onFormSubmit={onFormSubmit}
            />
          </Suspense>
        </DrawerContent>
      </Drawer>

      <TravellerCloseConfirmDrawer
        open={isCloseConfirmOpen}
        onClose={() => {
          setIsCloseConfirmOpen(false);
        }}
        onConfirm={() => {
          setIsCloseConfirmOpen(false);
          onClose();
        }}
      />
    </>
  );
}
